import React, { useState, useRef } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { FiX, FiImage, FiUpload } from "react-icons/fi";
import { useMyImages, useAllImages } from "@/hooks/useCertificate";
import { cn } from "@/lib/utils";
import { ImagePickerState, ImageTab } from "./types";
import { emptyImage, resolveUserAndOrg } from "./utils";
import { ImageUploadTab } from "@/components/collection/ImageUploadTab";
import { ImageGallery } from "@/components/collection/ImageGallery";
import { useAppI18n } from "@/hooks/useAppI18n";

const labelClass = "block text-sm font-medium text-sunbird-obsidian mb-1 font-rubik";

interface ImagePickerProps {
  label: string;
  required?: boolean;
  value: ImagePickerState;
  onChange: (v: ImagePickerState) => void;
}

export function ImagePickerDialog({ label, required, value, onChange }: ImagePickerProps) {
  const { t } = useAppI18n();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<ImageTab>("myImages");

  /* Upload tab state */
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [uploadFileName, setUploadFileName] = useState("");
  const [uploadCreator, setUploadCreator] = useState("");
  const [dragging, setDragging] = useState(false);

  const { data: myImages = [], isLoading: loadingMy } = useMyImages({
    enabled: open && tab === "myImages",
  });
  const { data: allImages = [], isLoading: loadingAll } = useAllImages({
    enabled: open && tab === "allImages",
  });

  /* ── Resolve creator name once when upload tab opens ── */
  const initUploadTab = async () => {
    if (uploadCreator) return;
    try {
      const { userName } = await resolveUserAndOrg();
      setUploadCreator(userName);
    } catch { /* ignore */ }
  };

  const handleTabChange = (t: ImageTab) => {
    setTab(t);
    if (t === "upload") initUploadTab();
  };

  /* ── File handling ── */
  const applyFile = (file: File) => {
    setUploadFile(file);
    setUploadFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setUploadPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) applyFile(f);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f && f.type.startsWith("image/")) applyFile(f);
  };

  const handleSelectImage = (url: string) => {
    onChange({ preview: url, artifactUrl: url, file: null });
    setOpen(false);
  };

  const handleUploadAndUse = () => {
    if (!uploadFile || !uploadPreview) return;
    onChange({ preview: uploadPreview, artifactUrl: null, file: uploadFile });
    setOpen(false);
    /* Reset upload tab state */
    setUploadFile(null);
    setUploadPreview(null);
    setUploadFileName("");
  };

  const handleBack = () => setTab("myImages");

  const tabBtn = (t: ImageTab, txt: string) => (
    <button
      key={t}
      type="button"
      onClick={() => handleTabChange(t)}
      className={cn(
        "flex-1 py-2.5 text-sm font-rubik font-medium relative transition-colors",
        tab === t ? "text-sunbird-theme-accent" : "text-muted-foreground hover:text-foreground"
      )}
    >
      {txt}
      {tab === t && (
        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-sunbird-theme-accent rounded-t-full" />
      )}
    </button>
  );

  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>

      {/* Trigger / preview */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "w-full rounded-lg border border-dashed border-border py-3 flex items-center justify-center gap-2 text-sm font-rubik transition-colors hover:border-sunbird-theme-accent hover:text-sunbird-theme-accent",
          value.preview ? "text-sunbird-theme-accent border-sunbird-theme-accent/40" : "text-muted-foreground"
        )}
      >
        {value.preview ? (
          <>
            <img src={value.preview} alt="selected" className="h-8 w-8 rounded object-cover" />
            <span className="text-xs">{t('imagePicker.changeImage')}</span>
          </>
        ) : (
          <>
            <FiImage className="w-4 h-4" />
            <span>{t('imagePicker.selectImage')}</span>
          </>
        )}
      </button>
      {value.preview && (
        <button
          type="button"
          onClick={() => onChange(emptyImage())}
          className="mt-1 text-xs text-red-400 hover:text-red-600 font-rubik"
        >
          {t('imagePicker.remove')}
        </button>
      )}

      {/* Child dialog */}
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[70] bg-black/50" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-[70] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white shadow-xl focus:outline-none overflow-hidden flex flex-col"
            style={{ width: "min(92vw, 38rem)", maxHeight: "80vh" }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
              <Dialog.Title className="text-base font-semibold text-sunbird-obsidian font-rubik">
                {t('imagePicker.selectImageTitle', { label })}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  className="rounded-lg p-1.5 text-muted-foreground hover:bg-gray-100 transition-colors"
                  aria-label="Close"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </Dialog.Close>
            </div>

            {/* Tabs */}
            {tab !== "upload" && (
               <div className="flex border-b border-border shrink-0">
                {([
                  { id: "myImages", label: t('imagePicker.myImages') },
                  { id: "allImages", label: t('imagePicker.allImages') },
                  { id: "upload", label: t('imagePicker.uploadImage') }
                ] as { id: ImageTab; label: string }[]).map((tab) => tabBtn(tab.id, tab.label))}
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* My Images */}
              {tab === "myImages" && (
                <div className="p-4">
                  <ImageGallery
                    loading={loadingMy}
                    images={myImages}
                    emptyMessage={t('imagePicker.noImagesUploaded')}
                    selectedUrl={value.artifactUrl}
                    onSelect={handleSelectImage}
                  />
                </div>
              )}

              {/* All Images */}
              {tab === "allImages" && (
                <div className="p-4">
                  <ImageGallery
                    loading={loadingAll}
                    images={allImages}
                    emptyMessage={t('imagePicker.noImagesFound')}
                    selectedUrl={value.artifactUrl}
                    onSelect={handleSelectImage}
                  />
                </div>
              )}

              {/* Upload Image */}
              {tab === "upload" && (
                <ImageUploadTab
                  dragging={dragging}
                  setDragging={setDragging}
                  handleDrop={handleDrop}
                  fileInputRef={fileInputRef}
                  uploadPreview={uploadPreview}
                  handleFileInput={handleFileInput}
                  uploadFileName={uploadFileName}
                  setUploadFileName={setUploadFileName}
                  uploadCreator={uploadCreator}
                  handleBack={handleBack}
                  handleCancel={() => setOpen(false)}
                  uploadFile={uploadFile}
                  handleUploadAndUse={handleUploadAndUse}
                  labelClass={labelClass}
                  inputClass="w-full rounded-lg border border-border px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-sunbird-theme-accent/40 focus:border-sunbird-theme-accent bg-white font-rubik"
                />
              )}
            </div>

            {/* Footer (My/All Images tabs) */}
            {tab !== "upload" && (
              <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-border shrink-0">
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="rounded-lg px-5 py-2 text-sm font-medium text-foreground bg-gray-100 hover:bg-gray-200 transition-colors font-rubik"
                  >
                    {t('imagePicker.cancel')}
                  </button>
                </Dialog.Close>
                <button
                  type="button"
                  onClick={() => handleTabChange("upload")}
                  className="rounded-lg px-5 py-2 text-sm font-medium text-sunbird-theme-accent border border-sunbird-theme-accent hover:bg-sunbird-theme-accent hover:text-white transition-colors font-rubik inline-flex items-center gap-2"
                >
                  <FiUpload className="w-4 h-4" />
                  {t('imagePicker.uploadNew')}
                </button>
              </div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
